import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Palette, Type, Image, Settings, Save, RotateCcw, Upload,
  LayoutDashboard, Users, DollarSign, ChevronRight,
  Menu, X, FileText, Code, Database, Activity, Sparkles, ExternalLink, Ticket
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import ColorPicker from '../components/admin/ColorPicker';
import FontSelector from '../components/admin/FontSelector';
import ImageUploader from '../components/admin/ImageUploader';
import HeroImageManager from '../components/admin/HeroImageManager';
import HeroTextManager from '../components/admin/HeroTextManager';
import ThemeImageManager from '../components/admin/ThemeImageManager';
import BackupRestore from '../components/admin/BackupRestore';
import { API_BASE_URL } from '../services/api';
import { useThemeTypes } from '../hooks/useThemeTypes';
import FinanceDashboard from '../components/admin/FinanceDashboard';
import UsersDashboard from '../components/admin/UsersDashboard';
import PromotionManager from '../components/admin/PromotionManager';
import CouponsManager from '../components/admin/CouponsManager';

type Section =
  | 'overview'
  | 'colors' | 'typography' | 'images' | 'hero' | 'hero-text'
  | 'content' | 'advanced' | 'backup'
  | 'finance' | 'users' | 'promotion' | 'coupons';

interface ThemeConfig {
  id?: number;
  name: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    cardBackground: string;
    textPrimary: string;
    textSecondary: string;
    accent: string;
  };
  typography: {
    fontFamilyPrimary: string;
    fontFamilyHeading: string;
    fontSizeBase: string;
    fontWeightNormal: string;
    fontWeightBold: string;
  };
  layout: {
    borderRadius: string;
    shadowIntensity: string;
    animationSpeed: string;
  };
  images: {
    logoUrl?: string;
    backgroundImageUrl?: string;
    cardBackImageUrl?: string;
    faviconUrl?: string;
  };
  heroText?: {
    title: string;
    subtitle: string;
    showTextDefault: boolean;
  };
  adminColors?: {
    backgroundFrom: string;
    backgroundVia: string;
    backgroundTo: string;
  };
  ctaColors?: {
    backgroundFrom: string;
    backgroundTo: string;
  };
  customCss?: string;
  features?: {
    title?: string;
    subtitle?: string;
    items?: { title?: string; description?: string; image?: string }[];
  };
  selection?: {
    title?: string;
    subtitle?: string;
  };
  tokens?: {
    title?: string;
    subtitle?: string;
    items?: { amount?: number; price?: string; description?: string; originalPrice?: string }[];
  };
}

type IconComponent = React.FC<{ className?: string; style?: React.CSSProperties }>;

interface NavGroup {
  label: string;
  items: { id: Section; label: string; icon: IconComponent }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'GERAL',
    items: [
      { id: 'overview', label: 'Visão Geral', icon: LayoutDashboard },
    ],
  },
  {
    label: 'VISUAL',
    items: [
      { id: 'colors', label: 'Cores', icon: Palette },
      { id: 'typography', label: 'Tipografia', icon: Type },
      { id: 'images', label: 'Imagens', icon: Image },
      { id: 'hero', label: 'Imagem Hero', icon: Upload },
      { id: 'hero-text', label: 'Texto Hero', icon: FileText },
    ],
  },
  {
    label: 'CONTEÚDO',
    items: [
      { id: 'content', label: 'Textos & Seções', icon: Settings },
      { id: 'advanced', label: 'CSS Avançado', icon: Code },
      { id: 'backup', label: 'Backup & Restore', icon: Database },
    ],
  },
  {
    label: 'GESTÃO',
    items: [
      { id: 'finance', label: 'Financeiro', icon: DollarSign },
      { id: 'users', label: 'Usuários', icon: Users },
      { id: 'promotion', label: 'Promoções', icon: Sparkles },
      { id: 'coupons', label: 'Cupons', icon: Ticket },
    ],
  },
];

const SECTION_TITLES: Record<Section, string> = {
  overview: 'Visão Geral',
  colors: 'Cores do Tema',
  typography: 'Tipografia',
  images: 'Imagens',
  hero: 'Imagem Hero',
  'hero-text': 'Texto Hero',
  content: 'Textos & Seções',
  advanced: 'CSS Avançado',
  backup: 'Backup & Restore',
  finance: 'Financeiro',
  users: 'Usuários',
  promotion: 'Promoções',
  coupons: 'Cupons',
};

const VISUAL_SECTIONS: Section[] = [
  'colors', 'typography', 'images', 'hero', 'hero-text',
  'content', 'advanced', 'backup',
];

// ── Design tokens do painel ─────────────────────────────────────────────────
const ui = {
  bg: '#0f172a',
  sidebar: '#0a0e1a',
  border: 'rgba(255,255,255,0.07)',
  card: 'rgba(255,255,255,0.04)',
  cardHover: 'rgba(255,255,255,0.07)',
  activeItem: 'rgba(59,130,246,0.15)',
  activeBorder: '#3b82f6',
  activeText: '#93c5fd',
  mutedText: '#6b7280',
  labelText: '#9ca3af',
  input: 'rgba(255,255,255,0.06)',
  inputBorder: 'rgba(255,255,255,0.1)',
};

const FEATURE_DEFAULTS = [
  'Tarot Rider-Waite', 'Astrologia Psicológica', 'Psicologia & Jung',
  'Arquétipos e Simbólicos', 'Amor e Relacionamentos', 'Prosperidade e Propósito',
  'Bem Estar e Saúde Emocional', 'Numerologia e Kabbalah', 'Karma e Destino', 'Caminhos Espirituais',
];

const FeatureItemsGrid: React.FC<{
  items?: { title?: string; description?: string; image?: string }[];
  token: string | null;
  inputCls: string;
  inputStyle: React.CSSProperties;
  labelCls: string;
  labelStyle: React.CSSProperties;
  uiBorder: string;
  onChange: (items: { title?: string; description?: string; image?: string }[]) => void;
}> = ({ items, token, inputCls, inputStyle, labelCls, labelStyle, uiBorder, onChange }) => {
  const [uploading, setUploading] = React.useState<number | null>(null);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const fileRefs = React.useRef<(HTMLInputElement | null)[]>(Array(10).fill(null));

  const getItems = () => {
    const base = Array.from({ length: 10 }, (_, i) => ({
      title: '', description: '', image: '', ...(items?.[i] || {}),
    }));
    return base;
  };

  const update = (idx: number, patch: Partial<{ title: string; description: string; image: string }>) => {
    const next = getItems();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const handleFileChange = async (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadError(null);
    setUploading(idx);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/hero/upload-feature-image/${idx}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erro no upload');
      }
      const { url } = await res.json();
      update(idx, { image: url });
    } catch (err: any) {
      setUploadError(err.message || 'Erro ao enviar imagem');
      setTimeout(() => setUploadError(null), 4000);
    } finally {
      setUploading(null);
    }
  };

  const currentItems = getItems();

  return (
    <div className="space-y-3">
      {uploadError && (
        <p className="text-red-400 text-xs px-1">{uploadError}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {currentItems.map((it, idx) => (
          <div key={idx} className="rounded-lg p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${uiBorder}` }}>
            <p className="text-xs font-semibold text-purple-300 mb-1">{idx + 1}. {FEATURE_DEFAULTS[idx]}</p>

            <div>
              <label className={labelCls} style={labelStyle}>Título</label>
              <input type="text" value={it.title || ''} placeholder={FEATURE_DEFAULTS[idx]}
                onChange={(e) => update(idx, { title: e.target.value })}
                className={inputCls} style={inputStyle} />
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Descrição</label>
              <textarea rows={2} value={it.description || ''}
                onChange={(e) => update(idx, { description: e.target.value })}
                className={inputCls} style={inputStyle} />
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Imagem de fundo</label>
              {it.image ? (
                <div className="flex items-center gap-2 mt-1">
                  <img src={it.image} alt="" className="w-16 h-10 object-cover rounded" />
                  <button
                    type="button"
                    onClick={() => update(idx, { image: '' })}
                    className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded"
                    style={{ background: 'rgba(255,0,0,0.1)' }}
                  >
                    Remover
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRefs.current[idx]?.click()}
                    className="text-xs text-purple-300 hover:text-white px-2 py-1 rounded"
                    style={{ background: 'rgba(139,92,246,0.2)' }}
                  >
                    Trocar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={uploading === idx}
                  onClick={() => fileRefs.current[idx]?.click()}
                  className="mt-1 flex items-center gap-1 text-xs text-purple-300 hover:text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50"
                  style={{ background: 'rgba(139,92,246,0.2)', border: `1px dashed ${uiBorder}` }}
                >
                  <Upload className="w-3 h-3" />
                  {uploading === idx ? 'Enviando…' : 'Enviar imagem'}
                </button>
              )}
              <input
                ref={(el) => { fileRefs.current[idx] = el; }}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFileChange(idx, e)}
                className="hidden"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { refreshTheme } = useTheme();
  const { themeTypes } = useThemeTypes();

  const [section, setSection] = useState<Section>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [editableTypes, setEditableTypes] = useState<any[]>([
    { id: null, key: 'amor', name: 'Amor', description: 'Descubra os segredos do seu coração e encontre respostas sobre relacionamentos, paixões e conexões profundas.' },
    { id: null, key: 'saude', name: 'Saúde', description: 'Obtenha orientações sobre bem-estar físico, mental e espiritual para uma vida mais equilibrada e saudável.' },
    { id: null, key: 'dinheiro', name: 'Dinheiro', description: 'Explore oportunidades financeiras, investimentos e caminhos para a prosperidade e abundância material.' },
  ]);

  const [currentTheme, setCurrentTheme] = useState<ThemeConfig>({
    name: 'Tema Personalizado',
    colors: {
      primary: '#8B5CF6',
      secondary: '#F59E0B',
      background: '#0F0F23',
      cardBackground: '#1A1A2E',
      textPrimary: '#FFFFFF',
      textSecondary: '#A0A0A0',
      accent: '#FF6B6B',
    },
    typography: {
      fontFamilyPrimary: "'Inter', sans-serif",
      fontFamilyHeading: "'Playfair Display', serif",
      fontSizeBase: '16px',
      fontWeightNormal: '400',
      fontWeightBold: '600',
    },
    layout: {
      borderRadius: '12px',
      shadowIntensity: 'medium',
      animationSpeed: '0.3s',
    },
    images: { logoUrl: undefined, backgroundImageUrl: undefined, cardBackImageUrl: undefined, faviconUrl: undefined },
    adminColors: { backgroundFrom: '#111827', backgroundVia: '#7C3AED', backgroundTo: '#8B5CF6' },
    ctaColors: { backgroundFrom: '#1F2937', backgroundTo: '#374151' },
    customCss: '',
    features: {
      title: 'POR QUE ESCOLHER NOSSO TAROT?',
      subtitle: 'Descubra o poder da sabedoria ancestral combinada com tecnologia moderna',
      items: [
        { title: 'IA Avançada', description: 'Interpretações precisas e personalizadas usando inteligência artificial de última geração' },
        { title: 'Múltiplos Temas', description: 'Consultas especializadas em amor, saúde e prosperidade financeira' },
        { title: 'Pagamento Seguro', description: 'Pagamentos rápidos e seguros via PIX com total privacidade' },
      ],
    },
    selection: {
      title: 'ESCOLHA SEU TEMA DE CONSULTA',
      subtitle: 'Selecione o tema que mais ressoa com suas necessidades atuais',
    },
    tokens: {
      title: 'Escolha seu pacote de Tokens',
      subtitle: 'Selecione um pacote e libere suas consultas com IA',
      items: [
        { amount: 30, price: 'R$ 19,90', description: 'Pacote essencial para começar' },
        { amount: 60, price: 'R$ 34,90', description: 'Melhor custo-benefício' },
        { amount: 90, price: 'R$ 49,90', description: 'Pacote avançado para uso frequente' },
      ],
    },
  });

  useEffect(() => { loadActiveTheme(); }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab) setSection(tab as Section);
    } catch {}
  }, []);

  useEffect(() => {
    if (themeTypes && themeTypes.length) {
      const byKey: Record<string, any> = {};
      themeTypes.forEach((t: any) => { byKey[t.key] = { id: t.id, key: t.key, name: t.name, description: t.description }; });
      const ordered = ['amor', 'saude', 'dinheiro'].map(k => byKey[k] ?? editableTypes.find(e => e.key === k));
      setEditableTypes(ordered.filter(Boolean) as any[]);
    }
  }, [themeTypes]);

  const loadActiveTheme = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/theme/active`);
      if (response.ok) {
        const theme = await response.json();
        if (theme.id !== 0) setCurrentTheme(theme);
      }
    } catch (error) {
      console.error('Erro ao carregar tema:', error);
    }
  };

  const saveTheme = async () => {
    setIsSaving(true);
    try {
      const themeData = {
        name: currentTheme.name,
        colors: currentTheme.colors,
        typography: currentTheme.typography,
        layout: currentTheme.layout,
        images: currentTheme.images,
        ctaColors: currentTheme.ctaColors,
        customCss: currentTheme.customCss || '',
        features: currentTheme.features,
        selection: currentTheme.selection,
        tokens: currentTheme.tokens,
      };

      const themeId = currentTheme.id;
      let savedTheme: any;

      if (themeId) {
        const response = await fetch(`${API_BASE_URL}/theme/configs/${themeId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(themeData),
        });
        if (!response.ok) {
          const err = await response.json();
          const detail = Array.isArray(err.detail)
            ? err.detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(' | ')
            : (err.detail || 'Erro ao atualizar tema');
          throw new Error(detail);
        }
        savedTheme = await response.json();
      } else {
        const response = await fetch(`${API_BASE_URL}/theme/configs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(themeData),
        });
        if (!response.ok) {
          const err = await response.json();
          const detail = Array.isArray(err.detail)
            ? err.detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(' | ')
            : (err.detail || 'Erro ao criar tema');
          throw new Error(detail);
        }
        savedTheme = await response.json();
        await fetch(`${API_BASE_URL}/theme/configs/${savedTheme.id}/activate`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }

      setCurrentTheme((prev: ThemeConfig) => ({ ...prev, id: savedTheme.id }));
      await refreshTheme();
      setMessage({ type: 'success', text: 'Tema salvo com sucesso!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao salvar tema:', error);
      const msg = error instanceof Error ? error.message : 'Erro ao salvar tema. Tente novamente.';
      setMessage({ type: 'error', text: msg });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const saveThemeTypes = async () => {
    if (!token) { setMessage({ type: 'error', text: 'Token não encontrado' }); return; }
    try {
      if (editableTypes.some(t => !t.id)) {
        await fetch(`${API_BASE_URL}/theme-types/initialize-defaults`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const resp = await fetch(`${API_BASE_URL}/theme-types/active`);
        const data = await resp.json();
        const map: Record<string, any> = {};
        (data?.theme_types ?? []).forEach((t: any) => { map[t.key] = t; });
        setEditableTypes(prev => prev.map(p => ({ ...p, id: map[p.key]?.id ?? p.id })));
      }
      for (const t of editableTypes) {
        await fetch(`${API_BASE_URL}/theme-types/${t.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ name: t.name, description: t.description }),
        });
      }
      setMessage({ type: 'success', text: 'Opções de temas atualizadas!' });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Erro ao atualizar opções de temas' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const resetToDefault = () => {
    setCurrentTheme(prev => ({
      ...prev,
      name: 'Tema Padrão',
      colors: {
        primary: '#8B5CF6', secondary: '#F59E0B', background: '#0F0F23',
        cardBackground: '#1A1A2E', textPrimary: '#FFFFFF', textSecondary: '#A0A0A0', accent: '#FF6B6B',
      },
      typography: {
        fontFamilyPrimary: "'Inter', sans-serif",
        fontFamilyHeading: "'Playfair Display', serif",
        fontSizeBase: '16px', fontWeightNormal: '400', fontWeightBold: '600',
      },
      layout: { borderRadius: '12px', shadowIntensity: 'medium', animationSpeed: '0.3s' },
      images: { logoUrl: undefined, backgroundImageUrl: undefined, cardBackImageUrl: undefined, faviconUrl: undefined },
      ctaColors: { backgroundFrom: '#1F2937', backgroundTo: '#374151' },
      customCss: '',
    }));
  };

  const updateHeroFadeCss = (existingCss: string, rgbaValue: string): string => {
    const rootBlockRegex = /:root\s*\{[\s\S]*?\}/m;
    const varLine = `  --hero-bottom-fade-to: ${rgbaValue};`;
    if (rootBlockRegex.test(existingCss)) {
      return existingCss.replace(rootBlockRegex, (block) => {
        if (/--hero-bottom-fade-to:\s*[^;]+;/.test(block)) {
          return block.replace(/--hero-bottom-fade-to:\s*[^;]+;/, varLine);
        }
        return block.replace(/\}/, `\n${varLine}\n}`);
      });
    }
    return `${existingCss ? existingCss + '\n' : ''}:root {\n${varLine}\n}`;
  };

  const isVisualSection = VISUAL_SECTIONS.includes(section);

  const navigateTo = (s: Section) => {
    setSection(s);
    setSidebarOpen(false);
  };

  // ── Sidebar ──────────────────────────────────────────────────────────────
  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : 'w-60'}`} style={{ background: ui.sidebar, borderRight: `1px solid ${ui.border}` }}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${ui.border}` }}>
        <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: '#3b82f6' }} />
        <div>
          <div className="text-white font-semibold text-sm leading-tight">Astralyz</div>
          <div className="text-xs" style={{ color: ui.mutedText }}>Painel Admin</div>
        </div>
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} className="ml-auto" style={{ color: ui.mutedText }}>
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mb-4">
            <div className="px-5 mb-1 text-[10px] font-semibold tracking-widest" style={{ color: ui.mutedText }}>
              {group.label}
            </div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = section === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => navigateTo(item.id)}
                  className="w-full flex items-center gap-2.5 px-5 py-2 text-sm transition-colors"
                  style={{
                    background: active ? ui.activeItem : 'transparent',
                    color: active ? ui.activeText : ui.labelText,
                    borderRight: active ? `2px solid ${ui.activeBorder}` : '2px solid transparent',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.color = '#e5e7eb'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.color = ui.labelText; e.currentTarget.style.background = 'transparent'; } }}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {active && <ChevronRight className="w-3 h-3 ml-auto flex-shrink-0 opacity-50" />}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Save button for visual sections */}
      {isVisualSection && (
        <div className="p-4" style={{ borderTop: `1px solid ${ui.border}` }}>
          <button
            onClick={saveTheme}
            disabled={isSaving}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-white text-sm font-medium transition-opacity disabled:opacity-50"
            style={{ background: '#2563eb' }}
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Salvando...' : 'Salvar Tema'}
          </button>
        </div>
      )}

      {/* User info */}
      <div className="px-5 py-3" style={{ borderTop: `1px solid ${ui.border}` }}>
        <p className="text-xs truncate" style={{ color: ui.mutedText }}>{user?.email ?? 'admin'}</p>
      </div>
    </div>
  );

  // ── Input / Label helpers ────────────────────────────────────────────────
  const inputCls = "w-full px-3 py-2 rounded-md text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500";
  const inputStyle = { background: ui.input, border: `1px solid ${ui.inputBorder}` };
  const labelCls = "block text-xs mb-1.5 font-medium";
  const labelStyle = { color: ui.labelText };
  const cardCls = "rounded-xl p-6";
  const cardStyle = { background: ui.card, border: `1px solid ${ui.border}` };

  // ── Conteúdo das seções ──────────────────────────────────────────────────
  const renderContent = () => {
    switch (section) {
      case 'overview':
        return <OverviewSection navigateTo={navigateTo} />;

      case 'colors':
        return (
          <div className="space-y-6">
            <ColorPicker
              colors={currentTheme.colors}
              onChange={(colors) => setCurrentTheme({ ...currentTheme, colors })}
            />

            {/* Gradiente CTA */}
            <div className={cardCls} style={cardStyle}>
              <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
                <Palette className="w-4 h-4" /> Gradiente da Seção CTA
              </h3>
              <p className="text-xs mb-5" style={{ color: ui.labelText }}>
                Fundo da seção "Pronto para descobrir seu destino?"
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(['backgroundFrom', 'backgroundTo'] as const).map((key, i) => (
                  <div key={key}>
                    <label className={labelCls} style={labelStyle}>{i === 0 ? 'Cor Inicial' : 'Cor Final'}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={currentTheme.ctaColors?.[key] || (i === 0 ? '#1F2937' : '#374151')}
                        onChange={(e) => setCurrentTheme({ ...currentTheme, ctaColors: { backgroundFrom: currentTheme.ctaColors?.backgroundFrom || '#1F2937', backgroundTo: currentTheme.ctaColors?.backgroundTo || '#374151', [key]: e.target.value } })}
                        className="w-9 h-9 rounded cursor-pointer"
                        style={{ border: `1px solid ${ui.inputBorder}`, background: 'transparent' }}
                      />
                      <input
                        type="text"
                        value={currentTheme.ctaColors?.[key] || (i === 0 ? '#1F2937' : '#374151')}
                        onChange={(e) => setCurrentTheme({ ...currentTheme, ctaColors: { backgroundFrom: currentTheme.ctaColors?.backgroundFrom || '#1F2937', backgroundTo: currentTheme.ctaColors?.backgroundTo || '#374151', [key]: e.target.value } })}
                        className={inputCls} style={inputStyle}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 h-12 rounded-lg" style={{ background: `linear-gradient(to right, ${currentTheme.ctaColors?.backgroundFrom || '#1F2937'}, ${currentTheme.ctaColors?.backgroundTo || '#374151'})`, border: `1px solid ${ui.border}` }} />
            </div>
          </div>
        );

      case 'typography':
        return (
          <FontSelector
            typography={currentTheme.typography}
            onChange={(typography) => setCurrentTheme({ ...currentTheme, typography })}
          />
        );

      case 'images':
        return (
          <div className="space-y-8">
            {/* Logo, verso das cartas e favicon */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-4">Logo, Cartas e Favicon</h3>
              <ImageUploader
                images={currentTheme.images}
                onChange={(images) => setCurrentTheme({ ...currentTheme, images })}
                token={token || undefined}
              />
            </div>

            {/* Divisor */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

            {/* Imagens dos temas Amor / Saúde / Dinheiro */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-1">Imagens dos Temas</h3>
              <p className="text-xs mb-4" style={{ color: '#6b7280' }}>Fundo das caixas Amor, Saúde e Dinheiro na página inicial</p>
              <ThemeImageManager
                onImageChange={(themeType, imageUrl) => console.log(`Imagem ${themeType}:`, imageUrl)}
                onTypographyChange={(typography) => console.log('Tipografia temas:', typography)}
              />
            </div>
          </div>
        );

      case 'hero':
        return (
          <HeroImageManager
            onImageChange={(imageUrl) => setCurrentTheme({ ...currentTheme, images: { ...currentTheme.images, backgroundImageUrl: imageUrl } })}
            onCustomCssChange={(rgba) => setCurrentTheme({ ...currentTheme, customCss: updateHeroFadeCss(currentTheme.customCss || '', rgba) })}
          />
        );

      case 'hero-text':
        return (
          <HeroTextManager
            onConfigChange={(config) => setCurrentTheme({ ...currentTheme, heroText: { title: config.title, subtitle: config.subtitle, showTextDefault: config.show_text_default } })}
          />
        );

      case 'content':
        return (
          <div className="space-y-6">
            {/* Features */}
            <div className={cardCls} style={cardStyle}>
              <h3 className="text-sm font-semibold text-white mb-4">Seção "Por que escolher nosso tarot?"</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Título</label>
                  <input type="text" value={currentTheme.features?.title || ''}
                    onChange={(e) => setCurrentTheme({ ...currentTheme, features: { ...(currentTheme.features || {}), title: e.target.value } })}
                    className={inputCls} style={inputStyle} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Subtítulo</label>
                  <input type="text" value={currentTheme.features?.subtitle || ''}
                    onChange={(e) => setCurrentTheme({ ...currentTheme, features: { ...(currentTheme.features || {}), subtitle: e.target.value } })}
                    className={inputCls} style={inputStyle} />
                </div>
              </div>
              <FeatureItemsGrid
                items={currentTheme.features?.items}
                token={token}
                inputCls={inputCls}
                inputStyle={inputStyle}
                labelCls={labelCls}
                labelStyle={labelStyle}
                uiBorder={ui.border}
                onChange={(items) => setCurrentTheme({ ...currentTheme, features: { ...(currentTheme.features || {}), items } })}
              />
            </div>

            {/* Selection */}
            <div className={cardCls} style={cardStyle}>
              <h3 className="text-sm font-semibold text-white mb-4">Seção "Escolha seu tema de consulta"</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Título</label>
                  <input type="text" value={currentTheme.selection?.title || ''}
                    onChange={(e) => setCurrentTheme({ ...currentTheme, selection: { ...(currentTheme.selection || {}), title: e.target.value } })}
                    className={inputCls} style={inputStyle} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Subtítulo</label>
                  <input type="text" value={currentTheme.selection?.subtitle || ''}
                    onChange={(e) => setCurrentTheme({ ...currentTheme, selection: { ...(currentTheme.selection || {}), subtitle: e.target.value } })}
                    className={inputCls} style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Theme Types */}
            <div className={cardCls} style={cardStyle}>
              <h3 className="text-sm font-semibold text-white mb-4">Opções de Temas (Amor, Saúde, Dinheiro)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {editableTypes.map((t, idx) => (
                  <div key={t.id ?? idx} className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${ui.border}` }}>
                    <label className={labelCls} style={labelStyle}>Título {idx + 1}</label>
                    <input type="text" value={t.name}
                      onChange={(e) => { const next = [...editableTypes]; next[idx] = { ...next[idx], name: e.target.value }; setEditableTypes(next); }}
                      className={`${inputCls} mb-3`} style={inputStyle} />
                    <label className={labelCls} style={labelStyle}>Descrição {idx + 1}</label>
                    <textarea rows={3} value={t.description}
                      onChange={(e) => { const next = [...editableTypes]; next[idx] = { ...next[idx], description: e.target.value }; setEditableTypes(next); }}
                      className={inputCls} style={inputStyle} />
                  </div>
                ))}
              </div>
              <button onClick={saveThemeTypes} className="px-4 py-2 rounded-md text-white text-sm font-medium transition-colors" style={{ background: '#2563eb' }}>
                Salvar Opções
              </button>
            </div>

            {/* Tokens */}
            <div className={cardCls} style={cardStyle}>
              <h3 className="text-sm font-semibold text-white mb-4">Pacotes de Tokens</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Título</label>
                  <input type="text" value={currentTheme.tokens?.title || ''}
                    onChange={(e) => setCurrentTheme({ ...currentTheme, tokens: { ...(currentTheme.tokens || {}), title: e.target.value } })}
                    className={inputCls} style={inputStyle} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Subtítulo</label>
                  <input type="text" value={currentTheme.tokens?.subtitle || ''}
                    onChange={(e) => setCurrentTheme({ ...currentTheme, tokens: { ...(currentTheme.tokens || {}), subtitle: e.target.value } })}
                    className={inputCls} style={inputStyle} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(currentTheme.tokens?.items || [{}, {}, {}]).map((it, idx) => (
                  <div key={idx} className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${ui.border}` }}>
                    <label className={labelCls} style={labelStyle}>Tokens</label>
                    <input type="number" value={it.amount ?? ''}
                      onChange={(e) => { const next = [...(currentTheme.tokens?.items || [{}, {}, {}])]; next[idx] = { ...next[idx], amount: Number(e.target.value) }; setCurrentTheme({ ...currentTheme, tokens: { ...(currentTheme.tokens || {}), items: next } }); }}
                      className={`${inputCls} mb-3`} style={inputStyle} />
                    <label className={labelCls} style={labelStyle}>Preço</label>
                    <input type="text" value={it.price || ''}
                      onChange={(e) => { const next = [...(currentTheme.tokens?.items || [{}, {}, {}])]; next[idx] = { ...next[idx], price: e.target.value }; setCurrentTheme({ ...currentTheme, tokens: { ...(currentTheme.tokens || {}), items: next } }); }}
                      className={`${inputCls} mb-3`} style={inputStyle} />
                    <label className={labelCls} style={labelStyle}>Preço "de" (riscado, opcional)</label>
                    <input type="text" placeholder="Ex: 39,90" value={it.originalPrice || ''}
                      onChange={(e) => { const next = [...(currentTheme.tokens?.items || [{}, {}, {}])]; next[idx] = { ...next[idx], originalPrice: e.target.value }; setCurrentTheme({ ...currentTheme, tokens: { ...(currentTheme.tokens || {}), items: next } }); }}
                      className={`${inputCls} mb-3`} style={inputStyle} />
                    <label className={labelCls} style={labelStyle}>Descrição</label>
                    <textarea rows={2} value={it.description || ''}
                      onChange={(e) => { const next = [...(currentTheme.tokens?.items || [{}, {}, {}])]; next[idx] = { ...next[idx], description: e.target.value }; setCurrentTheme({ ...currentTheme, tokens: { ...(currentTheme.tokens || {}), items: next } }); }}
                      className={inputCls} style={inputStyle} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'advanced':
        return (
          <div className="space-y-4">
            <div className={cardCls} style={cardStyle}>
              <label className={`${labelCls} mb-2`} style={labelStyle}>Nome do Tema</label>
              <input
                type="text"
                value={currentTheme.name}
                onChange={(e) => setCurrentTheme({ ...currentTheme, name: e.target.value })}
                className={inputCls} style={inputStyle}
              />
            </div>
            <div className={cardCls} style={cardStyle}>
              <label className={`${labelCls} mb-2`} style={labelStyle}>CSS Personalizado</label>
              <textarea
                value={currentTheme.customCss || ''}
                onChange={(e) => setCurrentTheme({ ...currentTheme, customCss: e.target.value })}
                rows={14}
                className={`${inputCls} font-mono`} style={inputStyle}
                placeholder="/* Adicione seu CSS personalizado aqui */"
              />
            </div>
          </div>
        );

      case 'backup':
        return (
          <BackupRestore
            currentConfig={currentTheme}
            onRestore={(theme: any) => setCurrentTheme(theme)}
            onSave={() => console.log('Salvando...')}
          />
        );

      case 'finance':
        return <FinanceDashboard />;

      case 'users':
        return <UsersDashboard />;

      case 'promotion':
        return <PromotionManager />;

      case 'coupons':
        return <CouponsManager />;

      default:
        return null;
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen overflow-hidden" style={{ background: ui.bg }}>
      {/* Sidebar desktop */}
      <div className="hidden lg:flex flex-col w-60 flex-shrink-0">
        <Sidebar />
      </div>

      {/* Sidebar mobile */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -240 }} animate={{ x: 0 }} exit={{ x: -240 }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="fixed left-0 top-0 bottom-0 w-60 z-50 lg:hidden flex flex-col"
            >
              <Sidebar mobile />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <div
          className="flex items-center gap-3 px-4 sm:px-6 py-3 flex-shrink-0"
          style={{ background: ui.sidebar, borderBottom: `1px solid ${ui.border}` }}
        >
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden" style={{ color: ui.mutedText }}>
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="text-white font-semibold text-sm">{SECTION_TITLES[section]}</h2>
          {isVisualSection && (
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={resetToDefault}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors"
                style={{ color: ui.labelText, background: 'rgba(255,255,255,0.05)', border: `1px solid ${ui.border}` }}
              >
                <RotateCcw className="w-3.5 h-3.5" /> Resetar
              </button>
              <button
                onClick={saveTheme}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-white text-xs font-medium transition-opacity disabled:opacity-50"
                style={{ background: '#2563eb' }}
              >
                <Save className="w-3.5 h-3.5" />
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {renderContent()}
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-5 right-5 px-4 py-3 rounded-lg shadow-xl text-sm font-medium z-50 text-white ${
              message.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}
          >
            {message.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Seção de Visão Geral ────────────────────────────────────────────────────
const QUICK_LINKS: { section: Section; label: string; desc: string; icon: IconComponent }[] = [
  { section: 'finance', label: 'Financeiro', desc: 'Receita e pagamentos', icon: DollarSign },
  { section: 'users', label: 'Usuários', desc: 'Contas e acessos', icon: Users },
  { section: 'coupons', label: 'Cupons', desc: 'Descontos e promoções', icon: Ticket },
  { section: 'colors', label: 'Cores', desc: 'Paleta do site', icon: Palette },
  { section: 'images', label: 'Imagens', desc: 'Logo, cartas e temas', icon: Image },
  { section: 'hero', label: 'Hero', desc: 'Imagem de destaque', icon: Upload },
  { section: 'content', label: 'Conteúdo', desc: 'Textos e seções', icon: FileText },
];

const cardBg = 'rgba(255,255,255,0.04)';
const cardBorder = 'rgba(255,255,255,0.07)';

const OverviewSection: React.FC<{ navigateTo: (s: Section) => void }> = ({ navigateTo }) => (
  <div className="space-y-8 max-w-4xl">
    <div>
      <h2 className="text-xl font-bold text-white mb-1">Painel Admin</h2>
      <p className="text-sm" style={{ color: '#6b7280' }}>Gerencie o visual, conteúdo e usuários do Astralyz.</p>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {QUICK_LINKS.map(({ section, label, desc, icon: Icon }) => (
        <button
          key={section}
          onClick={() => navigateTo(section)}
          className="rounded-xl p-5 text-left transition-colors group"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = cardBg; }}
        >
          <Icon className="w-5 h-5 mb-3" style={{ color: '#3b82f6' }} />
          <div className="text-white font-medium text-sm">{label}</div>
          <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{desc}</div>
        </button>
      ))}
    </div>

    <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-center gap-2 mb-3">
        <ExternalLink className="w-4 h-4" style={{ color: '#10b981' }} />
        <span className="text-white text-sm font-medium">Ferramentas Externas</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { href: 'https://analytics.google.com/', label: 'Google Analytics', desc: 'Acessos, sessões, países, páginas', domain: 'analytics.google.com' },
          { href: 'https://search.google.com/search-console', label: 'Search Console', desc: 'Indexação, palavras-chave e cliques', domain: 'search.google.com' },
          { href: 'https://eventsmanager.facebook.com/events_manager2/list/dataset/1559335968880821/overview?business_id=1344100694323067', label: 'Meta Pixel', desc: 'Eventos, conversões e audiências', domain: 'facebook.com' },
          { href: 'https://business.facebook.com/latest/settings/business_users/?business_id=1344100694323067', label: 'Meta Business', desc: 'Gerenciador de negócios do Facebook', domain: 'business.facebook.com' },
          { href: 'https://www.mercadopago.com.br/activities', label: 'Mercado Pago', desc: 'Pagamentos, PIX e extrato', domain: 'mercadopago.com.br' },
          { href: 'https://railway.app/project/86e8f66c-4455-4a9e-9d02-30ba52470115', label: 'Railway', desc: 'Logs, deploys e variáveis', domain: 'railway.app' },
          { href: 'https://platform.openai.com/usage', label: 'OpenAI Platform', desc: 'Consumo de tokens e custo Aurora', domain: 'openai.com' },
          { href: 'https://resend.com/emails', label: 'Resend', desc: 'E-mails transacionais enviados', domain: 'resend.com' },
          { href: 'https://improvmx.com/', label: 'ImprovMX', desc: 'Redirecionamento de e-mails', domain: 'improvmx.com' },
          { href: 'https://registro.br/', label: 'Registro.br', desc: 'DNS e domínio astralyz.com.br', domain: 'registro.br' },
        ].map(({ href, label, desc, domain }) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-3 px-4 py-3 rounded-lg transition-colors"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; }}
          >
            <img
              src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
              alt={label}
              className="w-8 h-8 flex-shrink-0 rounded-md"
            />
            <div>
              <div className="text-white text-sm font-medium">{label}</div>
              <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{desc}</div>
            </div>
          </a>
        ))}
      </div>
    </div>

    <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4" style={{ color: '#3b82f6' }} />
        <span className="text-white text-sm font-medium">Acesso Rápido</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {[
          { section: 'typography' as Section, label: 'Tipografia' },
          { section: 'advanced' as Section, label: 'CSS Avançado' },
          { section: 'backup' as Section, label: 'Backup & Restore' },
          { section: 'hero-text' as Section, label: 'Texto do Hero' },
        ].map(({ section, label }) => (
          <button
            key={section}
            onClick={() => navigateTo(section)}
            className="flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors text-left"
            style={{ color: '#9ca3af' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#e5e7eb'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
          >
            {label}
            <ChevronRight className="w-4 h-4 opacity-40 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  </div>
);

export default AdminDashboard;
